import {
  Page,
  Layout,
  ContextualSaveBar,
  Tabs,
  Banner,
  Card,
  Button,
} from "@shopify/polaris";
import React, { useEffect, useState, useContext } from "react";
import { Formik } from "formik";
import { nanoid } from "nanoid";
import { useToast } from "providers/ToastProvider";
import { useShop } from "providers/ShopProvider";
import { useLocation, useHistory } from "react-router-dom";
import { Flex, Box } from "rebass";
import { updateSettings as callUpdateSettings } from "services/wpApi";
import * as yup from "yup";
import IdCheckAppearance from "components/settings/IdCheckAppearance";
import AdditionalCheckRequirements from "components/settings/AdditionalCheckRequirements";
import IdCheckNotificationsSettings from "components/settings/IdCheckNotificationSettings";
import TermsAndConditions from "components/settings/TermsAndConditions";
import ResetTransients from "components/settings/ResetTransients";
import ConnectionStatus from "components/settings/ConnectionStatus";
import Billing from "components/settings/Billing";
import {
  FcSettings,
  FcClearFilters,
  FcEditImage,
  FcAbout,
  FcMoneyTransfer,
  FcShop,
  FcGlobe,
  FcInspection,
} from "react-icons/fc";
import DeactivateLicenseKey from "components/settings/DeactivateLicenseKey";
import Triggers from "components/settings/v2/Flows";
import OrderStatusSyncing from "./settings/IdCheckAdditionalRequirements/OrderStatusSyncing";
import ShipStationIntegration from "./settings/ShipStationIntegration";
import SembleIntegration from "./settings/SembleIntegration";
import WebhookSettings from "./settings/WebhookSettings";
import MultiSiteNetwork from "./settings/MultiSiteNetwork";
import getDefaultValues from "./settings/getDefaultValues";
import useAutoScroll from "hooks/useAutoScroll";

export default function Settings() {
  const setToast = useToast();
  const { shop, setShop } = useShop();
  const location = useLocation();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);

  // Tab mapping for URL query parameters
  const tabNameToIndex = {
    "automatic-triggers": 0,
    "appearance": 1,
    "automatic-rules": 2,
    "notifications": 3,
    "woocommerce": 4,
    "multi-site-network": 5,
    "billing": 6,
    "status": 7,
  };

  const indexToTabName = {
    0: "automatic-triggers",
    1: "appearance",
    2: "automatic-rules",
    3: "notifications",
    4: "woocommerce",
    5: "multi-site-network",
    6: "billing",
    7: "status",
  };

  // Get initial tab from URL or default to 0
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    return tabParam && tabNameToIndex.hasOwnProperty(tabParam) 
      ? tabNameToIndex[tabParam] 
      : 0;
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab);

  // Stable fallback webhook secret used as the form default when the shop
  // hasn't persisted one yet. Generating it inline in getDefaultValues caused
  // Formik to reinitialize on every Settings re-render (e.g. toast updates),
  // wiping the merchant's typed webhook URL.
  const [fallbackWebhookSecret] = useState(() => nanoid(16));

  // Update URL when tab changes
  const handleTabChange = (tabIndex) => {
    setSelectedTab(tabIndex);
    const tabName = indexToTabName[tabIndex];
    const searchParams = new URLSearchParams(location.search);
    
    if (tabName) {
      searchParams.set("tab", tabName);
    } else {
      searchParams.delete("tab");
    }
    
    // Update URL without refreshing the page
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Auto-scroll hook for deep linking
  useAutoScroll({
    queryParam: "scroll",
    delay: 500,
    dependencies: [selectedTab, shop],
  });

  // Update selected tab when URL changes (browser back/forward)
  useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== selectedTab) {
      setSelectedTab(newTab);
    }
  }, [location.search]);

  function updateSettings(values, setSubmitting) {
    setSubmitting(true);
    callUpdateSettings(values)
      .then(({ data }) => {
        setSubmitting(false);
        setShop(data);
      })
      .catch((err) => {
        Sentry.captureException(err);
        setToast(
          "Unable to update your shop settings. Please contact support."
        );
        setSubmitting(false);
      });
  }

  const tabs = [
    {
      id: "automatic-triggers",
      content: (
        //#637381
        <Flex alignItems="center">
          <FcSettings style={{ marginRight: "5px" }} />
          <span>Triggers</span>
        </Flex>
      ),
      accessibilityLabel: "Automatic ID triggers",
      panelID: "automatic-triggers-content-1",
    },
    {
      id: "appearance",
      content: (
        //#637381

        <Flex alignItems="center">
          <FcEditImage style={{ marginRight: "5px" }} />
          <span>Appearance</span>
        </Flex>
      ),
      accessibilityLabel: "ID check appearance",
      panelID: "Appearance",
    },
    {
      id: "automatic-rules",
      content: (
        //#637381
        <Flex alignItems="center">
          <FcClearFilters style={{ marginRight: "5px" }} />
          <span>Rules</span>
        </Flex>
      ),
      accessibilityLabel: "Automatic ID rules",
      panelID: "automatic-rules-content-1",
    },
    {
      id: "notifications",
      content: (
        //#637381

        <Flex alignItems="center">
          <FcAbout style={{ marginRight: "5px" }} />
          <span>Notifications</span>
        </Flex>
      ),
      accessibilityLabel: "Notifications on ID checks",
      panelID: "automatic-notifications-content-1",
    },
    {
      id: "woocommerce",
      content: (
        //#637381
        <Flex alignItems="center">
          <FcShop style={{ marginRight: "5px" }} />
          <span>WooCommerce</span>
        </Flex>
      ),
      accessibilityLabel: "WooCommerce details",
      panelID: "woocommerce-settings-content-1",
    },
    {
      id: "multi-site-network",
      content: (
        <Flex alignItems="center">
          <FcGlobe style={{ marginRight: "5px" }} />
          <span>Multi-Site</span>
        </Flex>
      ),
      accessibilityLabel: "Multi-site network settings",
      panelID: "multi-site-network-content-1",
    },
    {
      id: "billing",
      content: (
        //#637381
        <Flex alignItems="center">
          <FcMoneyTransfer style={{ marginRight: "5px" }} />
          <span>Billing</span>
        </Flex>
      ),
      accessibilityLabel: "Billing details",
      panelID: "automatic-billing-content-1",
    },
    {
      id: "status",
      content: (
        <Flex alignItems="center">
          <FcInspection style={{ marginRight: "5px" }} />
          <span>DevTools</span>
        </Flex>
      ),
      accessibilityLabel: "Connection status and diagnostics",
      panelID: "status-content-1",
    },
  ];

  return (
    <>
      <Page>
        {Array.isArray(shop?.settings?.deliveryMethods) &&
          shop?.settings?.deliveryMethods.includes("checkout") &&
          !shop?.settings?.automaticRemindersEnabled && (
            <Banner
              status="info"
              title="Automatically remind customers"
              // tab #3 contains notification settings
              action={{
                content: "Schedule reminders",
                onAction: () => setSelectedTab(3),
              }}
            >
              <p>
                Increase verification rates and save time by setting up a
                reminder schedule.
              </p>
              <p>
                Set up a schedule to remind customers to finish their ID checks.
              </p>
            </Banner>
          )}
        <Tabs
          tabs={tabs}
          selected={selectedTab}
          onSelect={handleTabChange}
        >
          <Box pt={3}>
            <Formik
              enableReinitialize
              initialValues={getDefaultValues(shop, fallbackWebhookSecret)}
              validationSchema={yup.object().shape({
                priceFloor: yup
                  .number()
                  .min(0)
                  .when("priceFloorEnabled", {
                    is: true,
                    then: yup.number().required("A price is required"),
                  }),
                // minimumAge: yup
                // .string()
                // .matches(
                //   /(disabled|legal_canadian_smoking_ages|21|18|\d+)/,
                //   "A valid age is required"
                // ) // weird stuff with the regex based rule, it doesn't seem to work properly
                // .required(),
                // more weird stuff, an empty string causes this to break
                // contactEmails: yup
                //   .array()
                //   .of(
                //     yup.string().email().required("A valid email is required")
                //   ),
              })}
              onSubmit={(values, { setSubmitting }) => {
                updateSettings(values, setSubmitting);
              }}
            >
              {({
                values,
                errors,
                dirty,
                handleBlur,
                handleSubmit,
                isSubmitting,
                isValid,
                resetForm,
                setFieldValue,
              }) => {
                return (
                  <Layout>
                    {selectedTab === 0 && <Triggers />}
                    {selectedTab === 1 && (
                      <IdCheckAppearance
                        values={values}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        errors={errors}
                        shop={shop}
                      />
                    )}

                    {selectedTab === 2 && (
                      <AdditionalCheckRequirements
                        values={values}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        errors={errors}
                        shop={shop}
                      />
                    )}
                    {selectedTab === 3 && (
                      <>
                        <IdCheckNotificationsSettings />
                      </>
                    )}
                    {selectedTab === 4 && (
                      <>
                        <Layout.AnnotatedSection
                          title="WooCommerce Integration Settings"
                          description={
                            <>
                              <p>
                                Customize how ID checks synchronize with
                                WooCommerce order statuses.
                              </p>
                            </>
                          }
                        >
                          <Card fullWidth={true}>
                            <OrderStatusSyncing />
                          </Card>
                        </Layout.AnnotatedSection>

                        <ShipStationIntegration
                          values={values}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          shop={shop}
                        />

                        <SembleIntegration
                          values={values}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          shop={shop}
                        />
                      </>
                    )}
                    {selectedTab === 5 && (
                      <MultiSiteNetwork />
                    )}
                    {selectedTab === 6 && (
                      <>
                        <Billing shop={shop} />
                        {!shop.sandboxMode && shop.licenseKey && (
                          <DeactivateLicenseKey />
                        )}
                        <TermsAndConditions />
                      </>
                    )}
                    {selectedTab === 7 && (
                      <>
                      <Layout.AnnotatedSection
                        title="Status & diagnostics"
                        description={
                          <p>
                            Check that Real ID can reach this site, view
                            recent plugin logs, or refresh the cached settings.
                          </p>
                        }
                      >
                        <Card fullWidth={true}>
                          <ConnectionStatus />
                        </Card>
                        <Card fullWidth={true}>
                          <Card.Section title="Real ID logs">
                            <p className="ri-mb-3">
                              Real ID writes its activity to your WooCommerce
                              logs. Check there if you're investigating a
                              specific verification or sharing context with
                              support.
                            </p>
                            <Button
                              url="/wp-admin/admin.php?page=wc-status&tab=logs&source=real_id&paged=1"
                              external
                            >
                              Open Real ID logs
                            </Button>
                          </Card.Section>
                        </Card>
                        <Card fullWidth={true}>
                          <ResetTransients />
                        </Card>
                      </Layout.AnnotatedSection>

                      <WebhookSettings
                        values={values}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        shop={shop}
                      />
                      </>
                    )}
                    {dirty && (
                      <>
                        <style jsx>{`
                          .Polaris-Frame__ContextualSaveBar {
                            top: 32px;
                          }
                        `}</style>

                        <ContextualSaveBar
                          message={
                            isValid
                              ? "Unsaved changes"
                              : Object.values(errors)[0]
                          }
                          fullWidth
                          alignContentFlush={false}
                          discardAction={{
                            content: "Discard",
                            onAction: resetForm,
                          }}
                          saveAction={{
                            content: "Save",
                            onAction: handleSubmit,
                            loading: isSubmitting,
                            disabled: !isValid,
                          }}
                        />
                      </>
                    )}
                  </Layout>
                );
              }}
            </Formik>
          </Box>
        </Tabs>
      </Page>
    </>
  );
}
